import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Clock, Download, CalendarIcon, Filter, Loader2, FileText, CheckCircle, 
  XCircle, AlertCircle, Save, Lock, Unlock, FileSpreadsheet, Edit, 
  Trash2, History
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractorTimesheets = () => {
  const [activeTab, setActiveTab] = useState("timesheets"); // timesheets, pending-approval, audit
  const [timesheets, setTimesheets] = useState([]);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [approvalStats, setApprovalStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [workers, setWorkers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [locks, setLocks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState("excel");
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [filters, setFilters] = useState({
    worker_id: "all",
    a_number: "all",
    start_date: null,
    end_date: null
  });
  
  // Cascading filter options
  const [filterOptions, setFilterOptions] = useState({
    a_numbers: [],
    workers: []
  });

  // Edit/Delete state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTimesheet, setDeletingTimesheet] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [auditDetailDialogOpen, setAuditDetailDialogOpen] = useState(false);
  const [selectedAuditLogs, setSelectedAuditLogs] = useState([]);

  // Approval state
  const [selectedForApproval, setSelectedForApproval] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingTimesheet, setRejectingTimesheet] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Fetch cascading filter options when filters change
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));
      
      const response = await axios.get(`${API}/timesheets/filter-options?${params.toString()}`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error("Failed to load filter options:", error);
    }
  }, [filters.a_number, filters.worker_id, filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchData = useCallback(async () => {
    try {
      const [tsRes, workRes, agreeRes, locksRes] = await Promise.all([
        axios.get(`${API}/timesheets`),
        axios.get(`${API}/users`),
        axios.get(`${API}/agreements`),
        axios.get(`${API}/timesheet-locks`)
      ]);
      setTimesheets(tsRes.data);
      setWorkers(workRes.data);
      setAgreements(agreeRes.data);
      setLocks(locksRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApprovalStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/timesheets/approval-stats`);
      setApprovalStats(response.data);
    } catch (error) {
      console.error("Failed to fetch approval stats:", error);
    }
  }, []);

  const fetchPendingTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/timesheets/pending-approval`);
      setPendingTimesheets(response.data);
    } catch (error) {
      toast.error("Failed to load pending timesheets");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/timesheet-audit`);
      setAuditLogs(response.data);
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchApprovalStats();
  }, [fetchData, fetchApprovalStats]);

  useEffect(() => {
    if (activeTab === "audit") {
      fetchAuditLogs();
    }
  }, [activeTab, fetchAuditLogs]);

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.project_id && filters.project_id !== "all") params.append("project_id", filters.project_id);
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/timesheets?${params.toString()}`);
      setTimesheets(response.data);
    } catch (error) {
      toast.error("Failed to filter timesheets");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/timesheets/export/pdf?${params.toString()}`, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `gavs_timesheets_${format(new Date(), "yyyyMMdd")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exported successfully");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const exportExcelOrCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.append("format", exportFormat);
      if (filters.a_number && filters.a_number !== "all") params.append("a_number", filters.a_number);
      if (filters.worker_id && filters.worker_id !== "all") params.append("worker_id", filters.worker_id);
      if (filters.start_date) params.append("start_date", format(filters.start_date, "yyyy-MM-dd"));
      if (filters.end_date) params.append("end_date", format(filters.end_date, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/timesheets/export/excel?${params.toString()}`, {
        responseType: "blob"
      });

      const ext = exportFormat === "excel" ? "xlsx" : "csv";
      const mimeType = exportFormat === "excel" 
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        : "text/csv";
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `gavs_timesheets_${format(new Date(), "yyyyMMdd")}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${exportFormat.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${exportFormat.toUpperCase()}`);
    }
  };

  const handleUnlockPeriod = async (year, month) => {
    setUnlockLoading(true);
    try {
      await axios.delete(`${API}/timesheet-locks/${year}/${month}`);
      toast.success(`Period ${year}-${String(month).padStart(2, '0')} unlocked successfully`);
      const locksRes = await axios.get(`${API}/timesheet-locks`);
      setLocks(locksRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock period");
    } finally {
      setUnlockLoading(false);
    }
  };

  // Edit timesheet
  const handleEditClick = (ts) => {
    setEditingTimesheet({ ...ts });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await axios.put(`${API}/timesheets/${editingTimesheet.id}/contractor-edit`, {
        start_time: editingTimesheet.start_time,
        end_time: editingTimesheet.end_time,
        location: editingTimesheet.location,
        worked_with: editingTimesheet.worked_with || "",
        notes: editingTimesheet.notes,
        detailed_comments: editingTimesheet.detailed_comments
      });
      toast.success("Timesheet updated successfully");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update timesheet");
    }
  };

  // Delete single timesheet
  const handleDeleteClick = (ts) => {
    setDeletingTimesheet(ts);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/timesheets/${deletingTimesheet.id}`);
      toast.success("Timesheet deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete timesheet");
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    try {
      const deleteParams = {};
      if (filters.start_date) deleteParams.start_date = format(filters.start_date, "yyyy-MM-dd");
      if (filters.end_date) deleteParams.end_date = format(filters.end_date, "yyyy-MM-dd");
      if (filters.a_number && filters.a_number !== "all") deleteParams.a_number = filters.a_number;
      if (filters.worker_id && filters.worker_id !== "all") deleteParams.worker_id = filters.worker_id;

      const response = await axios.post(`${API}/timesheets/bulk-delete`, deleteParams);
      toast.success(response.data.message);
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk delete");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // View audit history for a timesheet (Contractor only)
  const viewTimesheetAudit = async (timesheetId) => {
    try {
      const response = await axios.get(`${API}/timesheets/${timesheetId}/audit-history`);
      setSelectedAuditLogs(response.data);
      setAuditDetailDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load audit history");
    }
  };

  // Approval functions
  const approveTimesheet = async (timesheetId) => {
    setApprovalLoading(true);
    try {
      await axios.put(`${API}/timesheets/${timesheetId}/approve`, { action: "approve" });
      toast.success("Timesheet approved");
      fetchPendingTimesheets();
      fetchApprovalStats();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve timesheet");
    } finally {
      setApprovalLoading(false);
    }
  };

  const openRejectDialog = (ts) => {
    setRejectingTimesheet(ts);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setApprovalLoading(true);
    try {
      await axios.put(`${API}/timesheets/${rejectingTimesheet.id}/approve`, {
        action: "reject",
        rejection_reason: rejectionReason
      });
      toast.success("Timesheet rejected");
      setRejectDialogOpen(false);
      setRejectingTimesheet(null);
      setRejectionReason("");
      fetchPendingTimesheets();
      fetchApprovalStats();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject timesheet");
    } finally {
      setApprovalLoading(false);
    }
  };

  const bulkApprove = async () => {
    if (selectedForApproval.length === 0) {
      toast.error("Select at least one timesheet to approve");
      return;
    }
    setApprovalLoading(true);
    try {
      const response = await axios.post(`${API}/timesheets/bulk-approve`, {
        timesheet_ids: selectedForApproval,
        action: "approve"
      });
      toast.success(response.data.message);
      setSelectedForApproval([]);
      fetchPendingTimesheets();
      fetchApprovalStats();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk approve");
    } finally {
      setApprovalLoading(false);
    }
  };

  const toggleSelectForApproval = (timesheetId) => {
    if (selectedForApproval.includes(timesheetId)) {
      setSelectedForApproval(selectedForApproval.filter(id => id !== timesheetId));
    } else {
      setSelectedForApproval([...selectedForApproval, timesheetId]);
    }
  };

  const selectAllForApproval = () => {
    if (selectedForApproval.length === pendingTimesheets.length) {
      setSelectedForApproval([]);
    } else {
      setSelectedForApproval(pendingTimesheets.map(ts => ts.id));
    }
  };

  // Fetch pending when switching to approval tab
  useEffect(() => {
    if (activeTab === "pending-approval") {
      fetchPendingTimesheets();
    }
  }, [activeTab, fetchPendingTimesheets]);

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  // Memoize expensive filtered computations
  const lockedPeriods = useMemo(() => locks.filter(l => l.locked), [locks]);

  return (
    <div data-testid="contractor-timesheets">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[#333333]">TIMESHEETS</h1>
          <p className="text-[#52525B]">Manage, edit, delete and export timesheets</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-2">
        <Button 
          variant={activeTab === "timesheets" ? "default" : "ghost"}
          className={activeTab === "timesheets" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("timesheets")}
          data-testid="contractor-tab-timesheets"
        >
          <Clock className="w-4 h-4 mr-2" />
          Timesheets
        </Button>
        <Button 
          variant={activeTab === "pending-approval" ? "default" : "ghost"}
          className={activeTab === "pending-approval" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("pending-approval")}
          data-testid="contractor-tab-pending-approval"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Pending Approval
          {approvalStats.pending > 0 && (
            <Badge className="ml-2 bg-red-500 text-white">{approvalStats.pending}</Badge>
          )}
        </Button>
        <Button 
          variant={activeTab === "audit" ? "default" : "ghost"}
          className={activeTab === "audit" ? "bg-[#F37021] hover:bg-[#D95D15]" : ""}
          onClick={() => setActiveTab("audit")}
          data-testid="contractor-tab-audit"
        >
          <History className="w-4 h-4 mr-2" />
          Audit Log
        </Button>
      </div>

      {/* TIMESHEETS TAB */}
      {activeTab === "timesheets" && (
        <>
          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button className="btn-primary" onClick={exportPDF} data-testid="contractor-export-pdf">
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <div className="flex items-center gap-2">
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-24" data-testid="contractor-export-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={exportExcelOrCSV} data-testid="contractor-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export {exportFormat.toUpperCase()}
              </Button>
            </div>
            <Button 
              variant="destructive" 
              onClick={() => setBulkDeleteDialogOpen(true)}
              disabled={!filters.start_date && !filters.end_date && filters.a_number === "all" && filters.worker_id === "all"}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Bulk Delete
            </Button>
          </div>

          {/* Locked Periods Alert */}
          {lockedPeriods.length > 0 && (
            <Card className="mb-6 border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-orange-800">Locked Periods</h3>
                </div>
                <p className="text-sm text-orange-700 mb-3">
                  The following periods are locked. Team members cannot edit timesheets in these periods. 
                  You can unlock them if needed.
                </p>
                <div className="space-y-2">
                  {lockedPeriods.map((l) => (
                    <div key={`${l.year}-${l.month}`} className="flex items-center justify-between p-2 bg-white rounded border border-orange-200">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-red-600" />
                        <span className="font-medium">{months.find(m => m.value === l.month)?.label} {l.year}</span>
                        <span className="text-xs text-gray-500">Locked by {l.locked_by_name}</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleUnlockPeriod(l.year, l.month)}
                        disabled={unlockLoading}
                        data-testid={`unlock-${l.year}-${l.month}`}
                      >
                        {unlockLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
                        Unlock
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="input-label text-xs">A-Number</Label>
                  <Select
                    value={filters.a_number}
                    onValueChange={(value) => setFilters({ ...filters, a_number: value })}
                  >
                    <SelectTrigger data-testid="contractor-filter-a-number">
                      <SelectValue placeholder="All A-Numbers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All A-Numbers</SelectItem>
                      <SelectItem value="Depot">
                        <span className="flex items-center gap-2">
                          <span className="text-blue-500">Depot Work</span>
                        </span>
                      </SelectItem>
                      {/* Show only A-numbers that have entries matching current filters */}
                      {filterOptions.a_numbers.length > 0 ? (
                        filterOptions.a_numbers.map((a) => (
                          <SelectItem key={a.a_number} value={a.a_number}>
                            {a.a_number}{a.project_name ? ` - ${a.project_name}` : ''} ({a.entry_count})
                          </SelectItem>
                        ))
                      ) : (
                        // Fallback to all agreements if no filter options loaded yet
                        agreements.map((a) => (
                          <SelectItem key={a.id} value={a.a_number}>{a.a_number} - {a.project_name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label text-xs">Team Member</Label>
                  <Select
                    value={filters.worker_id}
                    onValueChange={(value) => setFilters({ ...filters, worker_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Team Members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Team Members</SelectItem>
                      {/* Show only workers that have entries matching current filters */}
                      {filterOptions.workers.length > 0 ? (
                        filterOptions.workers.map((w) => (
                          <SelectItem key={w.worker_id} value={w.worker_id}>
                            {w.worker_name} ({w.entry_count})
                          </SelectItem>
                        ))
                      ) : (
                        // Fallback to all workers if no filter options loaded yet
                        workers.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="input-label text-xs">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.start_date ? format(filters.start_date, "PPP") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.start_date}
                        onSelect={(date) => setFilters({ ...filters, start_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="input-label text-xs">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.end_date ? format(filters.end_date, "PPP") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.end_date}
                        onSelect={(date) => setFilters({ ...filters, end_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <Button onClick={applyFilters} className="mt-4 btn-secondary" data-testid="contractor-apply-filters">
                <Filter className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </CardContent>
          </Card>

          {/* Timesheets Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
                </div>
              ) : (
                <Table className="table-aggreko">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Worker</TableHead>
                      <TableHead>A-Number</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Approval</TableHead>
                      <TableHead>Lock Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timesheets.map((ts) => (
                      <TableRow key={ts.id} className={ts.is_locked ? "bg-gray-50" : ""}>
                        <TableCell>{ts.date}</TableCell>
                        <TableCell className="font-medium">{ts.worker_name}</TableCell>
                        <TableCell>
                          {ts.a_number ? (
                            <Badge variant="outline" className="text-xs">{ts.a_number}</Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell>{ts.agreement_name || ts.a_number}</TableCell>
                        <TableCell>{ts.start_time} - {ts.end_time}</TableCell>
                        <TableCell>{ts.location}</TableCell>
                        <TableCell>
                          {ts.approval_status === "approved" ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approved
                            </Badge>
                          ) : ts.approval_status === "rejected" ? (
                            <Badge className="bg-red-100 text-red-700" title={ts.rejection_reason}>
                              <XCircle className="w-3 h-3 mr-1" />
                              Rejected
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {ts.is_locked ? (
                            <Badge className="bg-red-100 text-red-700">
                              <Lock className="w-3 h-3 mr-1" />
                              Locked
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">
                              <Unlock className="w-3 h-3 mr-1" />
                              Open
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditClick(ts)} data-testid={`edit-ts-${ts.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(ts)} data-testid={`delete-ts-${ts.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => viewTimesheetAudit(ts.id)} data-testid={`audit-ts-${ts.id}`}>
                              <History className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {timesheets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-[#A1A1AA]">
                          No timesheets found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* PENDING APPROVAL TAB */}
      {activeTab === "pending-approval" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#F37021]" />
                Pending Approval ({pendingTimesheets.length})
              </div>
              {selectedForApproval.length > 0 && (
                <Button 
                  onClick={bulkApprove}
                  disabled={approvalLoading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="bulk-approve-btn"
                >
                  {approvalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Approve Selected ({selectedForApproval.length})
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Approval Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-600 uppercase">Pending</p>
                <p className="text-2xl font-bold text-yellow-700">{approvalStats.pending}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-xs text-green-600 uppercase">Approved</p>
                <p className="text-2xl font-bold text-green-700">{approvalStats.approved}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-xs text-red-600 uppercase">Rejected</p>
                <p className="text-2xl font-bold text-red-700">{approvalStats.rejected}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600 uppercase">Total</p>
                <p className="text-2xl font-bold text-gray-700">{approvalStats.total}</p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
              </div>
            ) : pendingTimesheets.length > 0 ? (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <Checkbox
                    checked={selectedForApproval.length === pendingTimesheets.length && pendingTimesheets.length > 0}
                    onCheckedChange={selectAllForApproval}
                  />
                  <span className="text-sm text-gray-600">Select All</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Worker</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>A-Number</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingTimesheets.map((ts) => (
                      <TableRow key={ts.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedForApproval.includes(ts.id)}
                            onCheckedChange={() => toggleSelectForApproval(ts.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{ts.worker_name}</TableCell>
                        <TableCell>{ts.date}</TableCell>
                        <TableCell>
                          <Badge className="bg-orange-100 text-[#F37021]">{ts.a_number || '-'}</Badge>
                          {ts.agreement_name && <p className="text-xs text-gray-500">{ts.agreement_name}</p>}
                        </TableCell>
                        <TableCell>
                          {ts.start_time} - {ts.end_time}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{ts.location}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveTimesheet(ts.id)}
                              disabled={approvalLoading}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-btn-${ts.id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openRejectDialog(ts)}
                              disabled={approvalLoading}
                              data-testid={`reject-btn-${ts.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <p className="text-lg text-gray-600">All timesheets are approved!</p>
                <p className="text-sm text-gray-500">No pending timesheets to review.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent aria-describedby="reject-dialog-description">
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p id="reject-dialog-description" className="text-sm text-gray-600">
              Please provide a reason for rejecting this timesheet. The worker will be notified.
            </p>
            {rejectingTimesheet && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium">{rejectingTimesheet.worker_name}</p>
                <p className="text-sm text-gray-600">{rejectingTimesheet.date} | {rejectingTimesheet.a_number}</p>
              </div>
            )}
            <Textarea
              placeholder="Rejection reason (required)"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmReject}
                disabled={approvalLoading || !rejectionReason.trim()}
              >
                {approvalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Reject Timesheet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AUDIT LOG TAB */}
      {activeTab === "audit" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#F37021]" />
              Audit Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Track all changes made to timesheets including creates, updates, deletes, locks, and unlocks.
            </p>

            {auditLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
              </div>
            ) : auditLogs.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead>Timesheet ID</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id || log.timestamp}>
                      <TableCell className="text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          log.action === "created" ? "bg-green-100 text-green-700" :
                          log.action === "updated" ? "bg-blue-100 text-blue-700" :
                          log.action === "deleted" ? "bg-red-100 text-red-700" :
                          log.action === "locked" ? "bg-orange-100 text-orange-700" :
                          "bg-yellow-100 text-yellow-700"
                        }>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>{log.changed_by_name}</TableCell>
                      <TableCell className="text-xs font-mono">{log.timesheet_id?.substring(0, 8)}...</TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {log.action === "deleted" && log.deleted_record ? (
                          <span className="text-red-600">
                            {log.deleted_record.worker_name} - {log.deleted_record.date}
                          </span>
                        ) : log.changes && Object.keys(log.changes).length > 0 ? (
                          <span className="max-w-xs truncate block">
                            {Object.keys(log.changes).join(", ")} changed
                          </span>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-gray-500 text-center py-8">No audit logs found</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Timesheet Entry</DialogTitle>
          </DialogHeader>
          {editingTimesheet && (
            <div className="space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Worker:</span>
                    <span className="ml-2 font-medium">{editingTimesheet.worker_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className="ml-2 font-medium">{editingTimesheet.date}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">A-Number:</span>
                    <span className="ml-2 font-medium text-[#F37021]">{editingTimesheet.a_number || "Depot"}</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Start Time</Label>
                  <Input 
                    type="time" 
                    value={editingTimesheet.start_time} 
                    onChange={(e) => setEditingTimesheet({...editingTimesheet, start_time: e.target.value})}
                    data-testid="edit-start-time"
                  />
                </div>
                <div>
                  <Label className="input-label">End Time</Label>
                  <Input 
                    type="time" 
                    value={editingTimesheet.end_time} 
                    onChange={(e) => setEditingTimesheet({...editingTimesheet, end_time: e.target.value})}
                    data-testid="edit-end-time"
                  />
                </div>
              </div>
              
              <div>
                <Label className="input-label">Location</Label>
                <Input 
                  value={editingTimesheet.location || ""} 
                  onChange={(e) => setEditingTimesheet({...editingTimesheet, location: e.target.value})}
                  placeholder="Work location"
                  data-testid="edit-location"
                />
              </div>
              
              <div>
                <Label className="input-label">Worked With</Label>
                <Input 
                  value={editingTimesheet.worked_with || ""} 
                  onChange={(e) => setEditingTimesheet({...editingTimesheet, worked_with: e.target.value})}
                  placeholder="Who did the worker work with?"
                  data-testid="edit-worked-with"
                />
              </div>
              
              <div>
                <Label className="input-label">Comments</Label>
                <Textarea 
                  value={editingTimesheet.detailed_comments || ""} 
                  onChange={(e) => setEditingTimesheet({...editingTimesheet, detailed_comments: e.target.value})}
                  placeholder="Detailed work description"
                  rows={2}
                  data-testid="edit-comments"
                />
              </div>
              
              <div>
                <Label className="input-label">Notes</Label>
                <Input 
                  value={editingTimesheet.notes || ""} 
                  onChange={(e) => setEditingTimesheet({...editingTimesheet, notes: e.target.value})}
                  placeholder="Additional notes"
                  data-testid="edit-notes"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={handleEditSave} data-testid="save-edit-button">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Timesheet</DialogTitle>
          </DialogHeader>
          {deletingTimesheet && (
            <div className="space-y-4">
              <p>Are you sure you want to delete this timesheet entry?</p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p><strong>Worker:</strong> {deletingTimesheet.worker_name}</p>
                <p><strong>Date:</strong> {deletingTimesheet.date}</p>
                <p><strong>Time:</strong> {deletingTimesheet.start_time} - {deletingTimesheet.end_time}</p>
              </div>
              <p className="text-sm text-gray-600">
                This action will be logged in the audit trail and can be reviewed later.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleDeleteConfirm}>Delete</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Delete Timesheets</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-red-600 font-semibold">Warning: This will delete ALL timesheets matching your current filters!</p>
            <div className="p-4 bg-gray-50 rounded-lg text-sm">
              <p><strong>Filters applied:</strong></p>
              {filters.start_date && <p>Start Date: {format(filters.start_date, "PPP")}</p>}
              {filters.end_date && <p>End Date: {format(filters.end_date, "PPP")}</p>}
              {filters.a_number !== "all" && <p>A-Number: {filters.a_number}</p>}
              {filters.worker_id !== "all" && <p>Worker ID: {filters.worker_id}</p>}
            </div>
            <p className="text-sm text-gray-600">
              All deleted timesheets will be logged in the audit trail for potential recovery.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleteLoading}>
                {bulkDeleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete All Matching
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Detail Dialog - Contractor Only */}
      <Dialog open={auditDetailDialogOpen} onOpenChange={setAuditDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#F37021]" />
              Edit History (Contractor Only)
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {selectedAuditLogs && selectedAuditLogs.length > 0 ? (
              <div className="space-y-3">
                {selectedAuditLogs.map((log) => (
                  <div key={log.id || log.timestamp} className="p-3 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <Badge className="bg-blue-100 text-blue-700">
                        Edited
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">By:</span> {log.changed_by_name}
                    </p>
                    {log.worker_name && (
                      <p className="text-xs text-gray-500 mb-2">
                        Worker: {log.worker_name} | Date: {log.timesheet_date} | A#: {log.a_number || "Depot"}
                      </p>
                    )}
                    {log.changes && Array.isArray(log.changes) && log.changes.length > 0 && (
                      <div className="mt-2 space-y-2 border-t pt-2">
                        <p className="text-xs font-medium text-gray-700">Changes:</p>
                        {log.changes.map((change) => (
                          <div key={change.field} className="text-xs bg-white p-2 rounded border">
                            <span className="font-medium text-gray-700">{change.field_label || change.field}:</span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-red-600 line-through">{change.old}</span>
                              <span className="text-gray-400">→</span>
                              <span className="text-green-600">{change.new}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Handle old format changes object */}
                    {log.changes && !Array.isArray(log.changes) && Object.keys(log.changes).length > 0 && (
                      <div className="mt-2 text-xs space-y-1 border-t pt-2">
                        {Object.entries(log.changes).map(([field, change]) => (
                          <div key={field} className="text-gray-600">
                            <span className="font-medium">{field}:</span>{" "}
                            {typeof change === 'object' && change.old !== undefined ? (
                              <><span className="text-red-600">{change.old || "(empty)"}</span> → <span className="text-green-600">{change.new || "(empty)"}</span></>
                            ) : (
                              String(change)
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No edit history for this timesheet</p>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorTimesheets;
